/**
 * Hook for managing auto-rate by location settings
 * Provides functionality to read, update, and preview auto-rate settings
 */

import { useState, useCallback } from 'react';
import { authenticatedFetch } from '@/lib/api';
import {
  AutoRateEntityType,
  AutoRateSetting,
  AutoRatePreviewResponse,
  AutoRateSettingUpdateResponse,
} from '@/types/rates';

const API_BASE = '/api/v1/admin/auto-rate-settings';

interface UseAutoRateSettingsOptions {
  /** Callback when setting is updated successfully */
  onUpdate?: (response: AutoRateSettingUpdateResponse) => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

interface UseAutoRateSettingsReturn {
  /** Current auto-rate setting */
  setting: AutoRateSetting | null;
  /** Preview data for a unit */
  preview: AutoRatePreviewResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Fetch current setting for an entity */
  fetchSetting: (entityType: AutoRateEntityType, entityId: string) => Promise<void>;
  /** Update setting for an entity */
  updateSetting: (
    entityType: AutoRateEntityType,
    entityId: string,
    value: boolean | null
  ) => Promise<AutoRateSettingUpdateResponse | null>;
  /** Fetch preview for a unit */
  fetchPreview: (unitId: string) => Promise<void>;
  /** Clear current state */
  clear: () => void;
}

/**
 * Hook for managing auto-rate by location settings
 */
export function useAutoRateSettings(
  options: UseAutoRateSettingsOptions = {}
): UseAutoRateSettingsReturn {
  const { onUpdate, onError } = options;

  const [setting, setSetting] = useState<AutoRateSetting | null>(null);
  const [preview, setPreview] = useState<AutoRatePreviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch current auto-rate setting for an entity
   */
  const fetchSetting = useCallback(async (
    entityType: AutoRateEntityType,
    entityId: string
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_BASE}/${entityType}/${entityId}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch auto-rate setting`);
      }

      const data = await response.json();
      setSetting(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [onError]);

  /**
   * Update auto-rate setting for an entity
   */
  const updateSetting = useCallback(async (
    entityType: AutoRateEntityType,
    entityId: string,
    value: boolean | null
  ): Promise<AutoRateSettingUpdateResponse | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_BASE}/${entityType}/${entityId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ autoRateByLocation: value }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update auto-rate setting`);
      }

      const data: AutoRateSettingUpdateResponse = await response.json();

      // Refresh the setting to get updated resolved values
      await fetchSetting(entityType, entityId);

      onUpdate?.(data);
      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      onError?.(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSetting, onUpdate, onError]);

  /**
   * Fetch preview of auto-assigned rate for a unit
   */
  const fetchPreview = useCallback(async (unitId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_BASE}/preview/${unitId}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch auto-rate preview`);
      }

      const data = await response.json();
      setPreview(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [onError]);

  /**
   * Clear current state
   */
  const clear = useCallback(() => {
    setSetting(null);
    setPreview(null);
    setError(null);
  }, []);

  return {
    setting,
    preview,
    isLoading,
    error,
    fetchSetting,
    updateSetting,
    fetchPreview,
    clear,
  };
}

/**
 * Get display text for the auto-rate setting inheritance
 */
export function getAutoRateInheritanceText(setting: AutoRateSetting): string {
  if (setting.enabled === null) {
    const status = setting.resolvedValue ? 'Enabled' : 'Disabled';
    return `Inherit from ${setting.resolvedFrom} (${status})`;
  }
  return setting.enabled ? 'Enabled' : 'Disabled';
}

/**
 * Get the toggle options for auto-rate settings
 * Companies have binary on/off, groups and units have inherit option
 */
export function getAutoRateToggleOptions(entityType: AutoRateEntityType): Array<{
  value: boolean | null;
  label: string;
}> {
  if (entityType === 'company') {
    return [
      { value: false, label: 'Disabled' },
      { value: true, label: 'Enabled' },
    ];
  }

  return [
    { value: null, label: 'Inherit' },
    { value: false, label: 'Disabled' },
    { value: true, label: 'Enabled' },
  ];
}

export default useAutoRateSettings;
